import { Policy, PolicyUpdateRequest, State } from "@bluefalcon/clients-management-client-fetch";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { carriersService, stateService } from "../../services";
import { Box, Heading, Flex, Text, SimpleGrid, useDisclosure, IconButton } from "@chakra-ui/react";
import { LoadingSpinner } from "@bluefalcon/components-package";
import { PolicyCountyTable } from "../../components/policy-county-table/policy-county-table.comp";
import { coverageTypes } from "../../constants/coverage-type.constants";
import { insuranceTypes } from "../../constants/insurace-type.constants";
import { AddEditPolicyModal } from "../../modals/add-edit-policy.modal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencil } from "@fortawesome/free-solid-svg-icons";

export function PolicyDetails() {
    const { policyId } = useParams();
    const navigate = useNavigate();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [policy, setPolicy] = useState<Policy>();
    const [states, setStates] = useState<State[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const getPolicyById = async () => {
        if (!policyId) {
            navigate('/carriers');
            return;
        }
        try {
            setIsLoading(true);
            const foundPolicy = await carriersService.getPolicyById(Number(policyId));
            setPolicy(foundPolicy);
        } catch (e: any) {
            navigate('/carriers');
        } finally {
            setIsLoading(false);
        }
    };

    const onUpdate = async (id: number, request: Omit<PolicyUpdateRequest, 'id'>) => {
        try {
            setIsLoading(true);
            await carriersService.updatePolicyById(id, {
                id,
                ...request,
            });
        } catch (error) { } finally {
            setIsLoading(false);
            onClose();
        }
    };

    const onDelete = async () => {
        try {
            setIsLoading(true);
            await carriersService.deletePolicyById(Number(policyId));
            navigate(`/carriers/${policy?.carrierId}`);
        } catch (error) { } finally {
            setIsLoading(false);
            onClose();
        }
    };

    const fetchStates = async () => {
        const res = await stateService.getStates();
        setStates(res);
    };

    useEffect(() => {
        getPolicyById();
        fetchStates();
    }, []);

    if (isLoading || !policy) {
        return <LoadingSpinner />
    }

    return (
        <Box p={4}>
            <Flex>
                <Heading mr={3} as={'h1'}>{policy.name}</Heading>
                <IconButton
                    bg={'transparent'}
                    aria-label="Edit Policy"
                    icon={<FontAwesomeIcon icon={faPencil} />}
                    onClick={onOpen}
                />
            </Flex>
            <Flex
                mt={5}
                borderWidth="1px"
                borderRadius="lg"
                p={6}
                boxShadow="md"
                mb={6}
                bg="white"
            >
                <SimpleGrid columns={[1, 2, 2]} spacing={4} width="100%">
                    <Flex>
                        <Text fontWeight="bold" mr={2}>
                            ID:
                        </Text>
                        <Text>{policy.id}</Text>
                    </Flex>
                    <Flex>
                        <Text fontWeight="bold" mr={2}>
                            Name:
                        </Text>
                        <Text>{policy.name}</Text>
                    </Flex>
                    <Flex>
                        <Text fontWeight="bold" mr={2}>
                            Carrier Codes:
                        </Text>
                        <Text>{policy.carrierCodes?.join(', ') ?? 'None'}</Text>
                    </Flex>
                    <Flex>
                        <Text fontWeight="bold" mr={2}>
                            Coverage Type:
                        </Text>
                        <Text>{coverageTypes[policy.coverageType]}</Text>
                    </Flex>
                    <Flex>
                        <Text fontWeight="bold" mr={2}>
                            Insurance Type:
                        </Text>
                        <Text>{insuranceTypes[policy.insuranceType]}</Text>
                    </Flex>
                    <Flex>
                        <Text fontWeight="bold" mr={2}>
                            States:
                        </Text>
                        <Text>{policy.states?.map((s) => s.id).join(', ') ?? 'None'}</Text>
                    </Flex>
                </SimpleGrid>
            </Flex>

            <Box mt={10}>
                <Heading size={'md'}>Counties</Heading>
                <PolicyCountyTable
                    policy={policy}
                    states={states}
                />
            </Box>
            <AddEditPolicyModal
                isOpen={isOpen}
                onClose={onClose}
                handleEditPolicy={onUpdate}
                handleAddPolicy={async () => { }}
                handleDeletePolicy={onDelete}
                policy={policy}
            />
        </Box>
    );
}
